import { useEffect, useState } from 'react';
import { apiService } from '../services/api';
import { User } from '../types';

type Result = {
  loading: boolean;
  hasAny: boolean | null;
};

/**
 * Minimal "has at least one appointment" check for customers.
 * Uses the existing `getCustomerAppointments` endpoint with `limit: 1`.
 *
 * Conservative behavior:
 * - On errors, returns `hasAny: false` (do not show eligibility-gated UI).
 */
export function useCustomerHasAnyAppointment(user: User | null, enabled: boolean): Result {
  const [loading, setLoading] = useState(false);
  const [hasAny, setHasAny] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!enabled || !user?.id) {
        setLoading(false);
        setHasAny(null);
        return;
      }

      try {
        setLoading(true);
        const res = await apiService.getCustomerAppointments({ limit: 1 });
        if (cancelled) return;
        setHasAny((res.data?.length || 0) > 0);
      } catch {
        if (cancelled) return;
        setHasAny(false);
      } finally {
        if (cancelled) return;
        setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [enabled, user?.id]);

  return { loading, hasAny };
}

